import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button } from '../../../../shared/components/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M08-010 §20 — Vendor communication centre for a job.
 *
 * Routed by the vendor assignment id; resolves jobRequestId from the assignment
 * detail. Lists messages (GET /v1/vendor/communications) and sends a new one
 * (POST /v1/vendor/communications) to a branding role.
 */
interface Message {
  readonly communicationId: string;
  readonly recipientRole: string;
  readonly subject: string;
  readonly messageText: string;
  readonly sentAt: string;
}

const RECIPIENTS = ['BRANDING_OFFICER', 'BRANDING_HEAD', 'SUPPORT'] as const;

export function VendorCommunicationPage(): ReactNode {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const { token } = useAuth();
  const { t } = useTranslation();
  // Instants follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();

  const [jobRequestId, setJobRequestId] = useState<string | null>(null);
  const [messages, setMessages] = useState<readonly Message[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const [recipientRole, setRecipientRole] = useState<(typeof RECIPIENTS)[number]>('BRANDING_OFFICER');
  const [subject, setSubject] = useState('');
  const [messageText, setMessageText] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadMessages = useCallback(async (job: string) => {
    const r = await apiRequest<{ items: Message[] }>(`/v1/vendor/communications?jobRequestId=${encodeURIComponent(job)}`, { token });
    setMessages(r.items);
  }, [token]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const asg = await apiRequest<{ jobRequestId: string }>(`/v1/vendor-assignments/${assignmentId}`, { token });
        if (!alive) return;
        setJobRequestId(asg.jobRequestId);
        await loadMessages(asg.jobRequestId);
        if (alive) setLoaded(true);
      } catch (e) {
        if (alive) { setError((e as Error).message); setLoaded(true); }
      }
    })();
    return () => { alive = false; };
  }, [assignmentId, token, loadMessages]);

  async function send() {
    setFormError(null);
    if (!subject.trim()) { setFormError(t('vops.communication.subjectRequired')); return; }
    if (!messageText.trim()) { setFormError(t('vops.communication.messageRequired')); return; }
    setBusy(true);
    try {
      await apiRequest('/v1/vendor/communications', {
        method: 'POST',
        token,
        body: {
          jobRequestId,
          vendorAssignmentId: assignmentId,
          recipientRole,
          subject: subject.trim(),
          messageText: messageText.trim(),
        },
      });
      setOk(true);
      setSubject('');
      setMessageText('');
      if (jobRequestId) await loadMessages(jobRequestId);
    } catch (e) {
      setFormError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!loaded) return <main aria-label="vendor-communication"><p>{t('common.loading')}</p></main>;

  return (
    <main aria-label="vendor-communication">
      <h1>{t('vops.communication.title')}</h1>
      <p><Link to={`/vendor-ops/jobs/${assignmentId}`} aria-label="back-to-job">{t('vops.common.backToJob')}</Link></p>

      {error ? <p role="alert">{error}</p> : null}

      <section aria-label="communication-thread">
        <h2>{t('vops.communication.messages')}</h2>
        {messages.length === 0 ? (
          <p aria-label="communication-empty">{t('vops.communication.empty')}</p>
        ) : (
          <ul aria-label="communication-list">
            {messages.map((m) => (
              <li key={m.communicationId} aria-label={`message-${m.communicationId}`}>
                <span aria-label={`message-recipient-${m.communicationId}`}>{m.recipientRole}</span>{' · '}
                <strong>{m.subject}</strong>
                <p>{m.messageText}</p>
                <span>{at(m.sentAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section aria-label="communication-compose">
        <h2>{t('vops.communication.compose')}</h2>
        {ok ? <p role="status" aria-label="send-result">{t('vops.communication.sent')}</p> : null}
        {formError ? <p role="alert">{formError}</p> : null}

        <label htmlFor="comm-recipient">{t('vops.communication.recipient')}</label>
        <select id="comm-recipient" aria-label="comm-recipient" value={recipientRole}
                onChange={(e) => setRecipientRole(e.target.value as (typeof RECIPIENTS)[number])} disabled={busy}>
          {RECIPIENTS.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>

        <label htmlFor="comm-subject">{t('vops.communication.subject')}</label>
        <input id="comm-subject" type="text" aria-label="comm-subject" value={subject}
               onChange={(e) => setSubject(e.target.value)} disabled={busy} />

        <label htmlFor="comm-message">{t('vops.communication.message')}</label>
        <textarea id="comm-message" aria-label="comm-message" value={messageText}
                  onChange={(e) => setMessageText(e.target.value)} disabled={busy} />

        <Button type="button" aria-label="send-btn" onClick={send} disabled={busy} variant="primary">{t('vops.communication.sendBtn')}</Button>
      </section>
    </main>
  );
}
